import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { retry, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment.prod';

// ✅ Add this interface at the top of data.service.ts (after imports)
export interface FarmerPayload {
  aadhaarNumber:      string;
  address:            string;
  age:                number | null;
  collectionPointId:  number;
  district:           string;
  farmerUniqueId:     number | null;
  firstName:          string;
  gender:             string;
  id:                 number | null;
  isActive:           boolean;
  lastName:           string;
  milkType:           string;
  nomineeName:        string;
  nomineePhoneNumber: string;
  phoneNumber1:       string;
  phoneNumber2:       string;
  pincode:            string;
  relation:           string;
  societyId:          number;
  state:              string;
  village:            string;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  url = environment.url;
  
  // Base URL without /tab for portal APIs
  private get portalBaseUrl(): string {
    return this.url.replace('/tab', '');
  }

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'hfskey': 'HFSAdmin@1'
    })
  };

  constructor(private http: HttpClient) {}

  // ✅ UPDATED: Login method
  login(credentials): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    console.log("Connecting..." + baseUrl + '/portal/login');
    
    const loginPayload = {
      username: credentials.username,
      password: credentials.password
    };
    
    return this.http.post(baseUrl + '/portal/login', JSON.stringify(loginPayload), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  // ========== PORTAL APIs - ALL use portalBaseUrl (without /tab) ==========
  
  /**
   * Get Unit Offices for dropdown
   */
  getUnitOffices(): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    console.log("Connecting..." + baseUrl + '/portal/getUnitOffices');
    return this.http.get(baseUrl + '/portal/getUnitOffices', this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get BCC/HQ by Unit Office ID
   */
  getBCCByUnitOffice(unitOfficeId: string): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    console.log("Connecting..." + baseUrl + '/portal/getBCCByUnitOffice/' + unitOfficeId);
    return this.http.get(baseUrl + '/portal/getBCCByUnitOffice/' + unitOfficeId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get All Societies for dropdown
   */
  getAllSocieties(): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    console.log("Connecting..." + baseUrl + '/portal/getAllSocieties');
    return this.http.get(baseUrl + '/portal/getAllSocieties', this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get All Collection Points for dropdown
   */
  getAllCollectionPoints(): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    console.log("Connecting..." + baseUrl + '/portal/getAllCollectionPoints');
    return this.http.get(baseUrl + '/portal/getAllCollectionPoints', this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get Societies by BCC/HQ ID
   */
  getSocietiesByBCC(bccId: string): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    console.log("Connecting..." + baseUrl + '/portal/getSocietiesByBCC/' + bccId);
    return this.http.get(baseUrl + '/portal/getSocietiesByBCC/' + bccId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get Collection Points by BCC/HQ ID
   */
  getCollectionPointsByBCC(bccId: string): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    console.log("Connecting..." + baseUrl + '/portal/getCollectionPointsByBCC/' + bccId);
    return this.http.get(baseUrl + '/portal/getCollectionPointsByBCC/' + bccId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get Societies by Unit Office ID
   */
  getSocietiesByUnitOffice(unitOfficeId: string): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    console.log("Connecting..." + baseUrl + '/portal/getSocietiesByUnitOffice/' + unitOfficeId);
    return this.http.get(baseUrl + '/portal/getSocietiesByUnitOffice/' + unitOfficeId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get Collection Points by Unit Office ID
   */
  getCollectionPointsByUnitOffice(unitOfficeId: string): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    console.log("Connecting..." + baseUrl + '/portal/getCollectionPointsByUnitOffice/' + unitOfficeId);
    return this.http.get(baseUrl + '/portal/getCollectionPointsByUnitOffice/' + unitOfficeId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get Variance Shiftwise Report
   * @param collectionPointId - Collection Point ID
   * @param milkType - Milk Type (0 for all, 49 for Cow, 50 for Buffalo based on your API)
   * @param shift - Shift (0 for all, 1 for AM, 2 for PM)
   * @param fromDate - Start Date (YYYY-MM-DD)
   * @param toDate - End Date (YYYY-MM-DD)
   * @param societyId - Society ID (optional, default 0)
   * @returns Observable with report data
   */
  getVarianceShiftwiseReport(
    collectionPointId: number | string,
    milkType: number | string,
    shift: number | string,
    fromDate: string,
    toDate: string,
    societyId: number | string = 0
  ): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    const api = `${baseUrl}/portal/getvarienceReport/${collectionPointId}/${societyId}/${milkType}/${shift}/${fromDate}/${toDate}`;
    
    console.log("Connecting to Variance Shiftwise Report API..." + api);
    
    return this.http.get(api, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Get Variance Shiftwise Report with full parameters
   * @param params - Object containing all parameters
   * @returns Observable with report data
   */
  getVarianceShiftwiseReportWithParams(params: {
    collectionPointId: number | string;
    societyId?: number | string;
    milkType: number | string;
    shift: number | string;
    fromDate: string;
    toDate: string;
  }): Observable<any> {
    const societyId = params.societyId || 0;
    return this.getVarianceShiftwiseReport(
      params.collectionPointId,
      params.milkType,
      params.shift,
      params.fromDate,
      params.toDate,
      societyId
    );
  }

  // ========== EXISTING METHODS - Keep as is ==========
  
  getDailyReport(): Observable<any> {
    console.log("Connecting..." + this.url + '/getDailymilkTransactionReport');
    return this.http.get(this.url + '/getDailymilkTransactionReport', this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getmilkTransactionReport(collectionpoint: string, shift_id: number, fromDate: string, toDate: string): Observable<any> {
    console.log("Connecting..." + this.url + '/getmilkTransactionReport/' + collectionpoint + '/' + shift_id + '/' + fromDate + ' 00:00:00/' + toDate + ' 23:59:59');
    return this.http.get(this.url + '/getmilkTransactionReport/' + collectionpoint + '/' + shift_id + '/' + fromDate + ' 00:00:00/' + toDate + ' 23:59:59', this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getSalesReport(societyId: number, milktype_id: number, fromDate: string, toDate: string): Observable<any> {
    console.log("Connecting..." + this.url + '/getSaleTransactionReport/' + societyId + '/' + milktype_id + '/' + fromDate + ' 00:00:00/' + toDate + ' 23:59:59');
    return this.http.get(this.url + '/getSaleTransactionReport/' + societyId + '/' + milktype_id + '/' + fromDate + ' 00:00:00/' + toDate + ' 23:59:59', this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getProducerProducts(producerId: string): Observable<any> {
    console.log("Connecting..." + this.url + '/products/' + producerId);
    return this.http.get(this.url + '/products/' + producerId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getPayoutReport(society_id: string, period: number, month: number): Observable<any> {
    console.log("Connecting..." + this.url + '/periodReport/' + society_id + '/' + period + '/' + month);
    return this.http.get(this.url + '/periodReport/' + society_id + '/' + period + '/' + month, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getshiftReport(shiftReport): Observable<any> {
    console.log("Connecting..." + this.url + '/getPortalShiftReport');
    return this.http.post(this.url + '/getPortalShiftReport', JSON.stringify(shiftReport), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  AddSalesTransaction(business): Observable<any> {
    console.log("add salesTransaction Connecting..." + this.url + '/saleTransaction');
    return this.http.post(this.url + '/saleTransaction', JSON.stringify(business), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  forgotPassword(Otp): Observable<any> {
    console.log("Connecting..." + this.url + '/generateOtp' + "/" + Otp.countryCode + "/" + Otp.mobileNo);
    return this.http.get(this.url + '/generateOtp' + "/" + Otp.countryCode + "/" + Otp.mobileNo, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  verifyotp(newOtp1): Observable<any> {
    console.log("Connecting..." + this.url + '/verifyOTP');
    return this.http.post(this.url + '/verifyOTP', JSON.stringify(newOtp1), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  newpassword(newOtp): Observable<any> {
    console.log("Connecting..." + this.url + '/forgotPassword');
    return this.http.post(this.url + '/forgotPassword', JSON.stringify(newOtp), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  saveCustomers(customer): Observable<any> {
    console.log("Connecting..." + this.url + '/saveCustomer');
    return this.http.post(this.url + '/saveCustomer', JSON.stringify(customer), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  saveBusiness(business): Observable<any> {
    console.log("Connecting..." + this.url + '/businessconfig');
    return this.http.post(this.url + '/businessconfig', JSON.stringify(business), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  saveMachineStatus(machineStatus): Observable<any> {
    console.log("MachineStatus..." + this.url + '/updateMen');
    return this.http.post(this.url + '/updateMen', JSON.stringify(machineStatus), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  AddStock(item): Observable<any> {
    console.log("addOrUpdateStock: Connecting..." + this.url + '/addOrUpdateStock', JSON.stringify(item));
    return this.http.post(this.url + '/addOrUpdateStock', JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getStock(merchantId: string): Observable<any> {
    console.log("Connecting..." + this.url + '/stockOfMerchant/' + merchantId);
    return this.http.get(this.url + '/stockOfMerchant/' + merchantId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  saveTech(business): Observable<any> {
    console.log("Connecting..." + this.url + '/techconfig');
    return this.http.post(this.url + '/techconfig', JSON.stringify(business), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getMachines(merchantId: string, fromno: number, count: number): Observable<any> {
    console.log("Connecting..." + this.url + '/machines/' + merchantId + '/' + fromno + '/' + count);
    return this.http.get(this.url + '/machines/' + merchantId + '/' + fromno + '/' + count, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getLockers(merchantId: string): Observable<any> {
    console.log("Connecting..." + this.url + '/lockers/' + merchantId);
    return this.http.get(this.url + '/lockers/' + merchantId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getLocks(masterLockerId: string): Observable<any> {
    console.log("Connecting..." + this.url + '/getLocks/' + masterLockerId);
    return this.http.get(this.url + '/getLocks/' + masterLockerId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getSprings(merchantId: string, machineId: string): Observable<any> {
    console.log("Connecting..." + this.url + '/springs/' + merchantId + '/' + machineId);
    return this.http.get(this.url + '/springs/' + merchantId + '/' + machineId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getMerchantTransactionsData(merchantId: string, machineId: string): Observable<any> {
    console.log("Stocks" + this.url + '/springs/' + merchantId + '/' + machineId);
    return this.http.get(this.url + '/springs/' + merchantId + '/' + machineId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getAllSpringsByMachine(posId): Observable<any> {
    console.log("Connecting..." + this.url + '/websprings/' + posId);
    return this.http.get(this.url + '/websprings/' + posId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getAllItemsOfMerchant(merchantId: string): Observable<any> {
    console.log("Connecting..." + this.url + '/itemsOfMerchant/' + merchantId);
    return this.http.get(this.url + '/itemsOfMerchant/' + merchantId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getAllStatusOfMachines(merchantId: string): Observable<any> {
    console.log("Connecting...(getAllStatusOfMachines)" + this.url + '/getMachinesCountByStatus/' + merchantId);
    return this.http.get(this.url + '/getMachinesCountByStatus/' + merchantId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getSoldItemDetails(merchantId: string): Observable<any> {
    console.log("Connecting...(getSoldItemDetails)" + this.url + '/getSoldItemDetails/' + merchantId);
    return this.http.get(this.url + '/getSoldItemDetails/' + merchantId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getTotalAmountCS1(collectionpoint: string): Observable<any> {
    console.log("Connecting..." + this.url + '/getTotalFatVolumeAmount/1/1/' + collectionpoint);
    return this.http.get(this.url + '/getTotalFatVolumeAmount/1/1/' + collectionpoint, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getTotalAmountCS2(collectionpoint: string): Observable<any> {
    console.log("Connecting..." + this.url + '/getTotalFatVolumeAmount/2/1/' + collectionpoint);
    return this.http.get(this.url + '/getTotalFatVolumeAmount/2/1/' + collectionpoint, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getTotalAmountBS1(collectionpoint: string): Observable<any> {
    console.log("Connecting..." + this.url + '/getTotalFatVolumeAmount/1/2/' + collectionpoint);
    return this.http.get(this.url + '/getTotalFatVolumeAmount/1/2/' + collectionpoint, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getTotalAmountBS2(collectionpoint: string): Observable<any> {
    console.log("Connecting..." + this.url + '/getTotalFatVolumeAmount/2/2/' + collectionpoint);
    return this.http.get(this.url + '/getTotalFatVolumeAmount/2/2/' + collectionpoint, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getTotalMerchantRevenueByDate(merchantRevenue): Observable<any> {
    console.log("Connecting..." + this.url + '/getTotalMerchantRevenueByDate/', JSON.stringify(merchantRevenue));
    return this.http.post(this.url + '/getTotalMerchantRevenueByDate/', JSON.stringify(merchantRevenue), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getMerchantTransactions(merchantId: string, machineId: string, noOfRows: number, pageNo: number): Observable<any> {
    console.log("Connecting..." + this.url + '/machineTransactions' + "/" + machineId);
    return this.http.get(this.url + '/machineTransactions' + "/" + machineId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  totalMerchantTransactions(merchantId: string, machineId: string): Observable<any> {
    console.log("Connecting..." + this.url + '/totalMerchantTransactions' + "/" + machineId);
    return this.http.get(this.url + '/totalMerchantTransactions' + "/" + machineId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getMerchantMachineTransactions(merchantId: string, noOfRows: number, pageNo: number): Observable<any> {
    console.log("Connecting..." + this.url + '/merchantTransactions' + "/" + merchantId + "/" + noOfRows + "/" + pageNo);
    return this.http.get(this.url + '/merchantTransactions' + "/" + merchantId + "/" + noOfRows + "/" + pageNo, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  totalMerchantMachineTransactions(merchantId: string): Observable<any> {
    console.log("Connecting..." + this.url + '/totalMerchantTransactions' + "/" + merchantId);
    return this.http.get(this.url + '/totalMerchantTransactions' + "/" + merchantId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getMerchantLockerTransactions(masterLockerId: string, noOfRows: number, pageNo: number): Observable<any> {
    console.log("Connecting..." + this.url + '/getMerchantLockerTransactions' + "/" + masterLockerId + "/" + noOfRows + "/" + pageNo);
    return this.http.get(this.url + '/getMerchantLockerTransactions' + "/" + masterLockerId + "/" + noOfRows + "/" + pageNo, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  totalMerchantLockerTransactions(masterLockerId: string): Observable<any> {
    console.log("Connecting..." + this.url + '/totalMerchantLockerTransactions' + "/" + masterLockerId);
    return this.http.get(this.url + '/totalMerchantLockerTransactions' + "/" + masterLockerId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  totalLockerransactions(masterLockerId: string): Observable<any> {
    console.log("Connecting..." + this.url + '/totalLockerransactions' + "/" + masterLockerId);
    return this.http.get(this.url + '/totalLockerransactions' + "/" + masterLockerId, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  loadItemsOfSpring(spring): Observable<any> {
    console.log("Connecting..." + this.url + '/loadItemsOfSpring', JSON.stringify(spring));
    return this.http.post(this.url + '/loadItemsOfSpring', JSON.stringify(spring), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  addOrUpdateStock(item): Observable<any> {
    console.log("addOrUpdateStock: Connecting..." + this.url + '/stock', JSON.stringify(item));
    return this.http.post(this.url + '/stock', JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  addOrUpdateItem(item): Observable<any> {
    console.log("Connecting..." + this.url + '/addOrUpdateItem', JSON.stringify(item));
    return this.http.post(this.url + '/addOrUpdateItem', JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getBankReport(
    collectionpointId: string,
    period: string,
    month: string,
    year: number
  ): Observable<any> {
    let fromDate: string;
    let toDate: string;

    if (period === '1') {
      fromDate = `${year}-${month}-01`;
      toDate = `${year}-${month}-15`;
    } else if (period === '2') {
      const lastDay = new Date(Number(year), Number(month), 0).getDate();
      fromDate = `${year}-${month}-16`;
      toDate = `${year}-${month}-${lastDay}`;
    } else {
      fromDate = `${year}-${month}-01`;
      const lastDay = new Date(Number(year), Number(month), 0).getDate();
      toDate = `${year}-${month}-${lastDay}`;
    }

    const encodedFromDate = encodeURIComponent(fromDate + ' 00:00:01');
    const encodedToDate = encodeURIComponent(toDate + ' 23:59:59');
    const apiURL = `${this.url}/getTotalTransactionDetails/${collectionpointId}/${encodedFromDate}/${encodedToDate}`;
    console.log('Connecting... ' + apiURL);

    return this.http.get(apiURL, this.httpOptions)
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  getRecoveriesReport(
    societyId: string,
    collectionPointId: string,
    pageNumber: number = 1,
    pageSize: number = 15,
    farmerId?: string,
    fromDate?: string,
    toDate?: string
  ): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    const baseApi = baseUrl + '/portal/getRecoveryTransactionDetails/' +
      societyId + '/' +
      collectionPointId + '/' +
      pageNumber + '/' +
      pageSize;

    let queryParams = '';
    const params = [];

    if (farmerId) {
      params.push(`farmerId=${farmerId}`);
    }
    
    if (fromDate) {
      params.push(`fromDate=${fromDate}`);
    }
    
    if (toDate) {
      params.push(`toDate=${toDate}`);
    }

    if (params.length > 0) {
      queryParams = '?' + params.join('&');
    }

    const api = baseApi + queryParams;
    console.log("Connecting to recoveries API..." + api);

    return this.http.get(api, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  getRecoveriesWithFilters(filters: {
    societyId: string;
    collectionPointId: string;
    pageNumber: number;
    pageSize: number;
    farmerId?: string;
    fromDate?: string;
    toDate?: string;
    recoveryType?: string;
  }): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    const baseApi = baseUrl + '/portal/getRecoveryTransactionDetails/' +
      filters.societyId + '/' +
      filters.collectionPointId + '/' +
      filters.pageNumber + '/' +
      filters.pageSize;

    const params = new URLSearchParams();
    
    if (filters.farmerId) {
      params.set('farmerId', filters.farmerId);
    }
    
    if (filters.fromDate) {
      params.set('fromDate', filters.fromDate);
    }
    
    if (filters.toDate) {
      params.set('toDate', filters.toDate);
    }
    
    if (filters.recoveryType) {
      params.set('recoveryType', filters.recoveryType);
    }

    const queryString = params.toString();
    const api = queryString ? `${baseApi}?${queryString}` : baseApi;
    
    console.log("Connecting to recoveries API with filters..." + api);

    return this.http.get(api, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

 /**
 * Get Statement Report - CORRECTED parameter order to match API
 * @param dairyId - Dairy ID (from hierarchy) - FIRST
 * @param unitOfficeId - Unit Office ID (from hierarchy) - SECOND
 * @param bccId - BCC ID (from hierarchy) - THIRD
 * @param chillerUnitId - Chiller Unit ID (from hierarchy) - FOURTH
 * @param societyId - Society ID (from hierarchy) - FIFTH
 * @param collectionPointId - Collection Point ID - SIXTH
 * @param milkTypeId - Milk Type (0=All, 1=Cow, 2=Buffalo) - SEVENTH
 * @param shiftId - Shift (0=All, 1=AM, 2=PM) - EIGHTH
 * @param fromDate - From Date (YYYY-MM-DD) - NINTH
 * @param toDate - To Date (YYYY-MM-DD) - TENTH
 */
getStatementReport(
  dairyId: number | string,        // ← MOVED TO FIRST
  unitOfficeId: number | string,   // ← MOVED TO SECOND
  bccId: number | string,          // ← MOVED TO THIRD
  chillerUnitId: number | string,  // ← MOVED TO FOURTH
  societyId: number | string,      // ← KEEP FIFTH
  collectionPointId: number | string,
  milkTypeId: number | string,
  shiftId: number | string,
  fromDate: string,
  toDate: string
): Observable<any> {
  const baseUrl = this.portalBaseUrl;
  // CORRECT URL ORDER: dairyId/unitOfficeId/bccId/chillerUnitId/societyId/collectionpointId/milktypeId/shiftId/fromDate/toDate
  const api = `${baseUrl}/portal/getStatementReport/${dairyId}/${unitOfficeId}/${bccId}/${chillerUnitId}/${societyId}/${collectionPointId}/${milkTypeId}/${shiftId}/${fromDate}/${toDate}`;
  
  console.log("Connecting to Statement Report API..." + api);
  
  return this.http.get(api, this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

/**
 * Get Day Book Report - Full version matching API endpoint
 * @param dairyId - Dairy ID (from hierarchy) - NOW FIRST
 * @param unitOfficeId - Unit Office ID (from hierarchy) - NOW SECOND
 * @param bccId - BCC ID (from hierarchy) - NOW THIRD
 * @param chillerUnitId - Chiller Unit ID (from hierarchy) - NOW FOURTH
 * @param societyId - Society ID (from hierarchy) - FIFTH
 * @param collectionPointId - Collection Point ID (user selected)
 * @param milkTypeId - Milk Type (0=All, 1=Cow, 2=Buffalo)
 * @param shiftId - Shift (0=All, 1=AM, 2=PM)
 * @param fromDate - From Date (YYYY-MM-DD)
 * @param toDate - To Date (YYYY-MM-DD)
 */
getDayBookReport(
  dairyId: number | string,        // ← FIRST
  unitOfficeId: number | string,   // ← SECOND
  bccId: number | string,          // ← THIRD
  chillerUnitId: number | string,  // ← FOURTH
  societyId: number | string,      // ← FIFTH
  collectionPointId: number | string,
  milkTypeId: number | string,
  shiftId: number | string,
  fromDate: string,
  toDate: string
): Observable<any> {
  const baseUrl = this.portalBaseUrl;
  // URL order: dairyId/unitOfficeId/bccId/chillerUnitId/societyId/...
  const api = `${baseUrl}/portal/getDailymilkTransactionReport/${dairyId}/${unitOfficeId}/${bccId}/${chillerUnitId}/${societyId}/${collectionPointId}/${milkTypeId}/${shiftId}/${fromDate}/${toDate}`;
  
  console.log("Connecting to Day Book Report API..." + api);
  
  return this.http.get(api, this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

/**
 * Get Day Book Report - Simplified version using selected collection point
 * @param collectionPoint - Selected collection point object (from hierarchy)
 * @param fromDate - From Date (YYYY-MM-DD)
 * @param toDate - To Date (YYYY-MM-DD)
 * @param milkTypeId - Milk Type (optional, default 0)
 * @param shiftId - Shift (optional, default 0)
 */
getSimpleDayBookReport(
  collectionPoint: any,
  fromDate: string,
  toDate: string,
  milkTypeId: number | string = 0,
  shiftId: number | string = 0
): Observable<any> {
  // Extract all IDs from the collection point object in the CORRECT ORDER
  const dairyId = collectionPoint.dairyId || 0;        // ← FIRST
  const unitOfficeId = collectionPoint.unitOfficeId || 0; // ← SECOND
  const bccId = collectionPoint.bccId || 0;            // ← THIRD
  const chillerUnitId = collectionPoint.chillerUnitId || 0; // ← FOURTH
  const societyId = collectionPoint.societyId || 0;    // ← FIFTH
  const collectionPointId = collectionPoint.collectionPointId;
  
  console.log('getSimpleDayBookReport - Extracted IDs (CORRECT ORDER):', {
    dairyId,        // Now first
    unitOfficeId,   // Now second
    bccId,          // Now third
    chillerUnitId,  // Now fourth
    societyId,      // Now fifth
    collectionPointId
  });
  
  // Pass parameters in the CORRECT ORDER to match getDayBookReport
  return this.getDayBookReport(
    dairyId.toString(),        // ← FIRST
    unitOfficeId.toString(),   // ← SECOND
    bccId.toString(),          // ← THIRD
    chillerUnitId.toString(),  // ← FOURTH
    societyId.toString(),      // ← FIFTH
    collectionPointId.toString(),
    milkTypeId.toString(),
    shiftId.toString(),
    fromDate,
    toDate
  );
}

  handleError(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(errorMessage);
  }

  /**
   * Get hierarchy data for a user
   * @param userId - User ID (empId)
   * @returns Observable with hierarchy data
   */
  getHierarchy(userId: string | number): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    const api = `${baseUrl}/portal/getHierarchy/${userId}`;
    
    console.log("Connecting to Hierarchy API..." + api);
    
    return this.http.get(api, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

   /**
 * Save BCC Shift Average
 * @param data - BCC sample data object with correct field names from Swagger
 * @returns Observable with API response
 */
saveBccShiftAvg(data: {
  amount: number | null;
  avgFat: number | null;
  avgSof: number | null;  // ⚠️ CHANGED: Field name is 'avgSof' not 'avgSnf'
  collectionDate: string;
  collectionPointId: number;
  employeeId: number;
  price: number | null;
  quantity: number | null;
  shiftLovId: number;
  societyId: number;
}): Observable<any> {
  const baseUrl = this.url; // Uses /tab endpoint
  const api = `${baseUrl}/saveBccShiftAvg`;
  
  console.log("Connecting to BCC Shift Avg API..." + api);
  console.log("Payload:", data);
  
  return this.http.post(api, JSON.stringify(data), this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

/**
   * Save Final Payment (Manual Entry)
   * @param data - Transaction data object for manual entry
   * @returns Observable with API response
   */
  saveFinalPayment(data: {
    amount: number | null;
    collectionpoint_id: number;
    entryType: string;
    farmer_id: number;
    fat_percent: number | null;
    localTxnId: string;
    mfat: number | null;
    milktype_id: number;
    msnf: number | null;
    mweight: number | null;
    pericePerLitre: number | null;
    quantityof_milk: number | null;
    shift_id: number;
    snf: number | null;
    transactionId: number;
    txnDate: string;
    update: string;
    updatedBy: string;
  }): Observable<any> {
    const baseUrl = this.url; // Uses /tab endpoint
    const api = `${baseUrl}/FinalPayment`;
    
    console.log("Connecting to FinalPayment API..." + api);
    console.log("Payload:", data);
    
    return this.http.post(api, JSON.stringify(data), this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
 * Get Producer Details for a given society and collection point
 * GET /portal/getProducerDetails/{societyId}/{collectionPointId}
 */
getProducerDetails(societyId: number | string, collectionPointId: number | string): Observable<any> {
  const baseUrl = this.portalBaseUrl;
  const api = `${baseUrl}/portal/getProducerDetails/${societyId}/${collectionPointId}`;

  console.log('Connecting to getProducerDetails API... ' + api);

  return this.http.get(api, this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}
getRateCard(collectionPointId: number): Observable<any> {
  const api = `${this.url}/getRateCard/${collectionPointId}`;
  console.log('Connecting to getRateCard API... ' + api);
  return this.http.get(api, this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

getProducerBankReport(
  dairyId: number | string,
  unitOfficeId: number | string,
  bccId: number | string,
  chillerUnitId: number | string,
  societyId: number | string,
  collectionPointId: number | string,
  milkTypeId: number | string,
  shiftId: number | string,
  year: number | string,
  month: number | string,
  cycle: number | string,
  farmerId: number | string = 0
): Observable<any> {
  const baseUrl = this.portalBaseUrl;
  const api = `${baseUrl}/portal/getProducerBankReport/${dairyId}/${unitOfficeId}/${bccId}/${chillerUnitId}/${societyId}/${collectionPointId}/${milkTypeId}/${shiftId}/${year}/${month}/${cycle}/${farmerId}`;
  console.log('Connecting to getProducerBankReport API... ' + api);
  return this.http.get(api, this.httpOptions).pipe(  // ← GET not POST
    retry(1),
    catchError(this.handleError)
  );
}
getProducerBankReportPost(payload: {
    bccId:             string;
    chillerUnitId:     string;
    collectionpointId: string;
    cycle:             number;
    dairyId:           number;
    farmerId:          number | null;
    milktypeId:        string;
    month:             number;
    shiftId:           string;
    societyId:         string;
    unitOfficeId:      string;
    year:              number;
  }): Observable<any> {
    const baseUrl = this.portalBaseUrl;
    const api = `${baseUrl}/portal/getProducerBankReport`;
    console.log('Connecting to getProducerBankReport (POST) API... ' + api);
    console.log('Payload:', payload);
    return this.http.post(api, JSON.stringify(payload), this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  
getProducerConsolidatedReport(payload: {
  bccId:             string;
  chillerUnitId:     string;
  collectionpointId: string | null;
  cycle:             number | null;
  dairyId:           number;
  farmerId:          number | null;
  milktypeId:        string;
  month:             number | null;
  shiftId:           string;
  societyId:         string;
  unitOfficeId:      string;
  year:              number;
}): Observable<any> {
  const baseUrl = this.portalBaseUrl;
  const api = `${baseUrl}/portal/producerConsolidated`;
  console.log('Connecting to producerConsolidated (POST) API... ' + api);
  console.log('Payload:', payload);
  return this.http.post(api, JSON.stringify(payload), this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

getAgentCommissionReport(payload: {
  agentId:           number | null;
  bccId:             string;
  chillerUnitId:     string;
  collectionpointId: string | null;
  cycle:             number;
  dairyId:           number;
  milktypeId:        string;
  month:             number;
  shiftId:           string;
  societyId:         string;
  unitOfficeId:      string;
  year:              number;
}): Observable<any> {
  const baseUrl = this.portalBaseUrl;
  const api = `${baseUrl}/portal/getAgentCommissionReport`;
  console.log('Connecting to getAgentCommissionReport (POST) API... ' + api);
  console.log('Payload:', payload);
  return this.http.post(api, JSON.stringify(payload), this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

getAgentCommissionDayWiseReport(payload: {
  agentId:           number | null;
  bccId:             string;
  chillerUnitId:     string;
  collectionpointId: string | null;
  cycle:             number;
  dairyId:           number;
  milktypeId:        string;
  month:             number;
  shiftId:           string;
  societyId:         string;
  unitOfficeId:      string;
  year:              number;
}): Observable<any> {
  const baseUrl = this.portalBaseUrl;
  const api = `${baseUrl}/portal/getAgentCommissionDayWiseReport`;
  console.log('Connecting to getAgentCommissionDayWiseReport (POST)... ' + api);
  console.log('Payload:', payload);
  return this.http.post(api, JSON.stringify(payload), this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

getDashboardSummary(payload: {
  bccId: string | null;
  chillerUnitId: string | null;
  collectionpointId: string | null;
  dairyId: number;
  date: string;
  milktypeId: string | null;
  shiftId: string | null;
  societyId: string;
  unitOfficeId: string;
}): Observable<any> {
  const baseUrl = this.portalBaseUrl;
  const api = `${baseUrl}/portal/dashboardSummary`;
  console.log('Connecting to dashboardSummary API... ' + api);
  console.log('Payload:', payload);
  return this.http.post(api, JSON.stringify(payload), this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

saveAndUpdateFarmer(payload: FarmerPayload): Observable<any> {
  const baseUrl = this.url.replace('/tab', '');
  const api = `${baseUrl}/producer/saveAndUpdateFarmer`;
  console.log('Connecting to saveAndUpdateFarmer API... ' + api);
  console.log('Payload:', payload);
  return this.http.post(api, JSON.stringify(payload), this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

getFarmerDetails(collectionPointId: number | string, farmerUniqueId: number | string): Observable<any> {
  const api = `${this.url}/getFarmerDetails/${collectionPointId}/${farmerUniqueId}`;
  console.log('Connecting to getFarmerDetails API... ' + api);
  return this.http.get(api, this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

getDaywisePeriodicStatement(payload: {
  bccId:             string;
  chillerUnitId:     string;
  collectionpointId: string;
  cycle:             number;
  dairyId:           number;
  milkTypeId:        string;
  month:             number;
  shiftId:           string;
  societyId:         string;
  unitOfficeId:      string;
  year:              number;
}): Observable<any> {
  const baseUrl = this.portalBaseUrl;
  const api = `${baseUrl}/portal/daywisePeriodicStatement`;
  console.log('Connecting to daywisePeriodicStatement (POST) API... ' + api);
  console.log('Payload:', payload);
  return this.http.post(api, JSON.stringify(payload), this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}

shiftClosure(payload: {
  action:            string;
  closureDate:       string;
  collectionPointId: number;
  empId:             number;
  remarks:           string;
  shiftLovId:        number;
}): Observable<any> {
  const baseUrl = this.portalBaseUrl;
  const api = `${baseUrl}/portal/shiftClosure`;
  console.log('Connecting to shiftClosure API... ' + api);
  console.log('Payload:', payload);
  return this.http.post(api, JSON.stringify(payload), this.httpOptions).pipe(
    retry(1),
    catchError((error: any) => {
      // ✅ For shift closure, return error response body instead of window.alert
      // This allows the component to handle "already closed" gracefully
      console.error('[ShiftClosure] HTTP Error:', error);
      if (error.error) {
        // Return the error body as a normal observable so component can handle it
        return of(error.error);
      }
      return of({ status: error.status, msg: error.message || 'An error occurred' });
    })
  );
}

saveFarmerBankDetails(payload: {
  accountNumber:  string;
  bankId:         number;
  bankName:       string;
  branch:         string;
  farmerId:       number;   // ← `id` from getProducerDetails response
  ifscCode:       string;
  isActive:       boolean;
  nomineeDetails: string;
}): Observable<any> {
  const baseUrl = this.url.replace('/tab', '');
  const api = `${baseUrl}/producer/saveFarmerBankDetails`;
  console.log('Connecting to saveFarmerBankDetails API... ' + api);
  console.log('Payload:', payload);
  return this.http.post(api, JSON.stringify(payload), this.httpOptions).pipe(
    retry(1),
    catchError(this.handleError)
  );
}
  
}

